/**
 * The approval journey's stations, and how each stage of it reads.
 *
 * @module components/governance/governance-stations
 */

import type { EditorProgrammeMetadata } from "@/platform";
import { steps, viewsOutputs } from "@/state/store";

/** Each section as its tab names it, for a requirement that points there. */
export const PROGRAMME_SECTION_LABELS: Record<string, string> = Object.fromEntries(
  [...steps, ...viewsOutputs].map((step) => [step.key, step.title]),
);

export function stageGuidance(workflowState: EditorProgrammeMetadata["workflowState"]): {
  label: string;
  responsible: string;
  nextStep: string;
  result: string;
} {
  switch (workflowState) {
    case "Draft":
      return {
        label: "Draft",
        responsible: "Programme Lead or Client Admin",
        nextStep:
          "Complete Identity, Outcomes (or CAS components), Delivery and Modules. Stages and awards can be added later.",
        result: "When all setup requirements pass, the programme is ready for development.",
      };
    case "Ready for Development":
      return {
        label: "Development, not started",
        responsible: "Programme Lead or Client Admin",
        nextStep: "Assign the first Module Builder to a specific module in Programme Teams.",
        result: "That assignment starts development automatically.",
      };
    case "In Development":
      return {
        label: "Development, in progress",
        responsible: "Programme Lead",
        nextStep:
          "Resolve every requirement listed here, then select Submit for internal review below.",
        result:
          "The programme moves to Awaiting Internal Review and the submitted revision is fixed.",
      };
    case "Awaiting Internal Review":
      return {
        label: "Awaiting Internal Review",
        responsible: "Assigned reviewer",
        nextStep: "Open this programme and select Start review below.",
        result: "The programme moves to Internal Review and review findings can be recorded.",
      };
    case "Internal Review":
      return {
        label: "Internal Review",
        responsible: "Assigned reviewer",
        nextStep:
          "Resolve all blocking findings, add a comment, then select Mark reviewed. Use Request changes when the programme team must revise content.",
        result: "Mark reviewed moves the programme to Awaiting QA Approval.",
      };
    case "Changes Required":
      return {
        label: "Changes Required",
        responsible: "Programme Lead and programme team",
        nextStep:
          "Open each linked finding, complete the requested work, then select Resume development below.",
        result: "The programme returns to development and must be submitted for review again.",
      };
    case "Awaiting QA Approval":
      return {
        label: "Awaiting QA Approval",
        responsible: "Client Admin",
        nextStep:
          "Confirm the approval label and effective date, then select Approve programme. To send it back instead, a reviewer in the current round selects Request changes.",
        result: "Approval creates the approved revision and moves the programme to Approved.",
      };
    case "Approved":
      return {
        label: "Approved",
        responsible: "Programme Lead and QA signatories",
        nextStep:
          "Record the required Programme Lead and QA sign-offs, then select Complete final descriptor sign-off.",
        result: "The approved descriptor becomes ready for final export.",
      };
    case "Final Descriptor Signed Off":
      return {
        label: "Final Descriptor Signed Off",
        responsible: "Programme Lead",
        nextStep: "Choose the export format, then select Export and lock.",
        result: "The exported revision becomes locked against further editing.",
      };
    case "Exported and Locked":
      return {
        label: "Exported and Locked",
        responsible: "Client Admin",
        nextStep:
          "Use Portfolio > Manage to open a revalidation, amendment or replacement programme when change is required.",
        result:
          "The approved curriculum remains locked while work continues in a governed revision.",
      };
    case "Archived":
      return {
        label: "Archived",
        responsible: "Nobody",
        nextStep: "None. An archived programme cannot move to another state.",
        result: "The programme never reached export, and nothing further happens to it.",
      };
    default:
      return {
        label: workflowState,
        responsible: "Programme governance team",
        nextStep: "Review the available actions and requirements below.",
        result: "The next state depends on the action recorded for this programme.",
      };
  }
}

export const APPROVAL_STATIONS = [
  { key: "development", label: "Development", icon: "pencil-simple" },
  { key: "internal-review", label: "Internal review", icon: "magnifying-glass" },
  { key: "qa-approval", label: "QA approval", icon: "seal-check" },
  { key: "approved", label: "Approved", icon: "check-circle" },
  { key: "final-sign-off", label: "Final sign-off", icon: "signature" },
  { key: "locked", label: "Exported", icon: "lock-key" },
] as const;

export function approvalStationIndex(
  workflowState: EditorProgrammeMetadata["workflowState"],
): number {
  switch (workflowState) {
    case "Awaiting Internal Review":
    case "Internal Review":
      return 1;
    case "Awaiting QA Approval":
      return 2;
    case "Approved":
      return 3;
    case "Final Descriptor Signed Off":
      return 4;
    case "Exported and Locked":
      return 5;
    // Archived holds no station of its own; the journey ends at `archivedFrom`.
    case "Archived":
      return -1;
    default:
      return 0;
  }
}
